import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, Download, MoreVertical, MapPin, Phone, Mail, Calendar } from 'lucide-react';

const EnquiryList = () => {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchEnquiries = async () => {
            try {
                const res = await axios.get('http://localhost:5000/api/enquiry');
                setEnquiries(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchEnquiries();
    }, []);

    const filteredEnquiries = enquiries.filter(e => 
        e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.phone && e.phone.includes(searchTerm))
    );

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input 
                        type="text" 
                        placeholder="Search by name, service or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-gold-primary outline-none transition-all"
                    />
                </div>
                <div className="flex space-x-3 w-full md:w-auto">
                    <button className="flex-grow md:flex-grow-0 flex items-center justify-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                        <Filter size={18} />
                        <span className="font-semibold text-gray-700">Filter</span>
                    </button>
                    <button className="flex-grow md:flex-grow-0 flex items-center justify-center space-x-2 px-6 py-3 bg-dark-green text-white rounded-xl hover:bg-dark-green-deep transition-colors">
                        <Download size={18} />
                        <span className="font-semibold text-white">Export CSV</span>
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Customer</th>
                                <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Service Details</th>
                                <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Location</th>
                                <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Date & Time</th>
                                <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-8 py-10 text-center text-gray-500">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-dark-green mx-auto"></div>
                                        <p className="mt-4">Loading enquiries...</p>
                                    </td>
                                </tr>
                            ) : filteredEnquiries.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-8 py-10 text-center text-gray-500">
                                        No enquiries found matching your search.
                                    </td>
                                </tr>
                            ) : (
                                filteredEnquiries.map((e) => (
                                    <tr key={e._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-8 py-5">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-dark-green">{e.name}</span>
                                                <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                                                    <Phone size={12} />
                                                    <span>{e.phone}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex flex-col">
                                                <span className={`inline-block px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider mb-1 w-fit
                                                    ${e.service === 'Loan' ? 'bg-blue-100 text-blue-700' : 
                                                      e.service === 'Insurance' ? 'bg-purple-100 text-purple-700' :
                                                      'bg-green-100 text-green-700'}
                                                `}>
                                                    {e.service}
                                                </span>
                                                {e.loanType && (
                                                    <span className="text-sm font-medium text-gray-700">{e.loanType} - ₹{e.loanAmount}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                <MapPin size={14} />
                                                <span>{e.city}, {e.state}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                <Calendar size={14} />
                                                <span>{formatDate(e.createdAt)}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 uppercase">
                                                New
                                            </span>
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                            <button className="text-gray-400 hover:text-dark-green transition-colors p-1">
                                                <MoreVertical size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default EnquiryList;
